import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Send, CheckCircle2, User, Phone, Clock, MessageSquare } from "lucide-react";

export default function EnquiryForm({ propertyId, propertyTitle }) {
  const [form, setForm] = useState({ buyer_name: "", buyer_phone: "", preferred_time: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const nav = useNavigate();

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.buyer_name.trim() || !form.buyer_phone.trim()) {
      toast.error("Naam aur phone bharen");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", { property_id: propertyId, ...form });
      setSubmitted(true);
      toast.success("Enquiry submit ho gayi! BhoomiMitra jaldi call karega.");
    } catch (err) {
      const st = err.response?.status;
      if (st === 401) {
        toast.error("Enquiry bhejne ke liye login karein");
        nav("/login", { state: { from: `/property/${propertyId}` } });
        return;
      }
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mt-4 p-5 rounded-lg bg-primary/5 border border-primary/30 text-center" data-testid="enquiry-success">
        <CheckCircle2 size={32} className="text-primary mx-auto mb-2" />
        <div className="font-semibold">Dhanyawaad!</div>
        <p className="text-xs text-muted-foreground mt-1">
          Aapki enquiry BhoomiMitra ko WhatsApp par bhej di gayi hai. Kuch hi der me call ayegi.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-4 p-4 rounded-lg bg-card border border-border" data-testid="enquiry-form">
      <div className="overline text-secondary text-[9px] mb-1">Or leave your details</div>
      <div className="text-sm font-semibold mb-3">BhoomiMitra aapko call karega</div>
      <div className="space-y-2.5">
        <div>
          <Label className="text-xs flex items-center gap-1"><User size={11} />Aapka Naam</Label>
          <Input
            value={form.buyer_name}
            onChange={(e) => upd("buyer_name", e.target.value)}
            className="mt-1 h-9 text-sm"
            placeholder="e.g. Rahul Sharma"
            required
            data-testid="enquiry-name"
          />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><Phone size={11} />Phone Number</Label>
          <Input
            value={form.buyer_phone}
            onChange={(e) => upd("buyer_phone", e.target.value)}
            className="mt-1 h-9 text-sm"
            placeholder="10-digit mobile"
            required
            data-testid="enquiry-phone"
          />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><Clock size={11} />Call karne ka time (optional)</Label>
          <Input
            value={form.preferred_time}
            onChange={(e) => upd("preferred_time", e.target.value)}
            className="mt-1 h-9 text-sm"
            placeholder="e.g. Aaj sham 5 baje"
            data-testid="enquiry-time"
          />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><MessageSquare size={11} />Message (optional)</Label>
          <Textarea
            rows={2}
            value={form.message}
            onChange={(e) => upd("message", e.target.value)}
            className="mt-1 text-sm"
            placeholder="Kya jaanna chahte hain?"
            data-testid="enquiry-message"
          />
        </div>
      </div>
      <Button
        type="submit"
        disabled={submitting}
        className="w-full mt-3 bg-primary hover:bg-primary/90 rounded-full"
        data-testid="enquiry-submit"
      >
        <Send size={13} className="mr-1.5" />
        {submitting ? "Bheja ja raha hai..." : "Send Enquiry to BhoomiMitra"}
      </Button>
      <p className="text-[10px] text-muted-foreground text-center mt-2">
        Property: <span className="font-mono">{propertyId}</span>
      </p>
    </form>
  );
}
